import { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Plus, Printer, X } from 'lucide-react';
import Button from '../../components/Button';
import { useThermalPrinter } from '../../context/ThermalPrinterContext';
import { ProductsContext } from '../../context/ProductsContext';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency } from '../../data/mockData';
import { getPosSale, normalizePosSalePayload } from '../../api/pos';
import { printReceiptBrowser } from '../../utils/printReceiptBrowser';
import {
  POS_COUNTER, POS_GSTIN, isPosReceiptRevised, isPosReceiptVoided, posReceiptRevisionLabel,
} from '../../utils/escpos';
import {
  calculateBillSavings,
  formatDiscountLabel,
  formatDiscountPercent,
  formatItemSavingsText,
  getItemMrp,
  getItemSavings,
  payLaterCashierNotes,
  payLaterHeadline,
  payLaterLedger,
  payLaterMethodLines,
  receiptTenderLines,
  TENDER_LABEL,
} from '../../utils/posMoney';
import {
  formatItemsQtySummary, formatLineQty, unitPriceLabel,
} from '../../utils/soldBy';
import bhimaMartLogo from '../../assets/bhima mart logo black.svg';
import styles from './Receipt.module.css';

const PAYMENT_LABEL = TENDER_LABEL;

export default function Receipt({
  receipt: initialReceipt,
  changeDue = 0,
  token,
  mode = 'sale',
  autoPrint = false,
  onNewSale,
  onClose,
  closeLabel = 'Close',
  products: propProducts = [],
}) {
  const { user } = useAuth();
  const productsCtx = useContext(ProductsContext);
  const catalogProducts = (propProducts && propProducts.length > 0)
    ? propProducts
    : (productsCtx?.products || []);
  const { connected, printReceipt } = useThermalPrinter();
  const [receipt, setReceipt] = useState(initialReceipt);
  const [reprinting, setReprinting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [reprintError, setReprintError] = useState('');
  const [printNotice, setPrintNotice] = useState('');
  const [printWarning, setPrintWarning] = useState('');
  const [printAttempted, setPrintAttempted] = useState(mode === 'reprint');
  const autoPrintDone = useRef(false);

  const isReprintMode = mode === 'reprint';
  const isProvisional = Boolean(receipt.provisional) && !receipt.order_id;
  const isConfirmedSale = Boolean(receipt.order_id);
  const isVoided = isPosReceiptVoided(receipt);
  const isRevised = isPosReceiptRevised(receipt);
  const revisionLabel = posReceiptRevisionLabel(receipt);

  useEffect(() => {
    setReceipt(initialReceipt);
  }, [initialReceipt]);

  const dt = new Date(receipt.created_at);
  const dateShort = dt.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
  const timeShort = dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const counter = receipt.counter || POS_COUNTER;
  const gstin = receipt.gstin || POS_GSTIN;
  const cashierName = String(
    receipt.cashier_name || receipt.cashierName || user?.name || '',
  ).trim() || '—';
  const customerNumber = receipt.customer_number || '—';
  const customerPhone = receipt.customer_phone || receipt.phone || null;
  const customerName = receipt.customer_name || null;
  const isPayLater = String(receipt.payment_method || '').toUpperCase() === 'PAY_LATER';
  const payLater = isPayLater ? payLaterLedger(receipt) : null;
  const payLaterCollected = Boolean(payLater?.collected);
  const payLaterNote = isPayLater ? payLaterHeadline(receipt, formatCurrency) : '';
  const methodBreakdown = payLater ? payLaterMethodLines(payLater.paidBy) : [];
  const cashierNotes = isPayLater ? payLaterCashierNotes(receipt) : [];
  const tenderLines = isPayLater ? [] : receiptTenderLines(receipt);
  const addressLine1 = receipt.address_line1 || null;
  const addressLine2 = receipt.address_line2 || null;
  const block = receipt.block || null;
  const flat = receipt.flat || null;
  const roundOff = Number(receipt.round_off) || 0;
  const amountReturned = Number(receipt.amount_returned) || 0;
  const taxedSlabs = (receipt.gst_breakup || []).filter((g) => g.rate > 0);
  const totalGst = taxedSlabs.reduce((sum, g) => sum + g.tax, 0);
  const formatRoundOff = (amount) => {
    const n = Number(amount) || 0;
    if (n > 0) return `+${formatCurrency(n)}`;
    return formatCurrency(n);
  };
  const lineItems = receipt.line_items || [];
  const itemsQtySummary = formatItemsQtySummary(lineItems);
  const billSavings = calculateBillSavings(receipt, catalogProducts);
  const printFrameRef = useRef(null);

  const printViaBrowser = () => {
    printFrameRef.current?.remove();
    printFrameRef.current = printReceiptBrowser(document.getElementById('pos-receipt'));
  };

  useEffect(() => {
    const cleanupPrintFrame = () => {
      printFrameRef.current?.remove();
      printFrameRef.current = null;
    };

    window.addEventListener('afterprint', cleanupPrintFrame);
    return () => {
      window.removeEventListener('afterprint', cleanupPrintFrame);
      cleanupPrintFrame();
    };
  }, []);

  const runPrint = async (nextReceipt) => {
    setPrinting(true);
    setPrintNotice('');
    setPrintWarning('');
    setPrintAttempted(true);
    const printable = {
      ...nextReceipt,
      cashier_name: nextReceipt?.cashier_name || nextReceipt?.cashierName || user?.name || undefined,
    };

    if (connected) {
      try {
        await printReceipt(printable, {
          changeDue,
          logoSrc: bhimaMartLogo,
          products: catalogProducts,
        });
        setPrintNotice('Sent to thermal printer.');
        return;
      } catch {
        setPrintWarning('Thermal print failed — opening system print dialog.');
        printViaBrowser();
        return;
      } finally {
        setPrinting(false);
      }
    }

    printViaBrowser();
    setPrinting(false);
  };

  useEffect(() => {
    if (autoPrintDone.current) return undefined;
    if (!autoPrint && (isReprintMode || !connected)) return undefined;
    let cancelled = false;
    let attempts = 0;
    const tryPrint = () => {
      if (cancelled || autoPrintDone.current) return;
      if (!document.getElementById('pos-receipt') && attempts < 5) {
        attempts += 1;
        requestAnimationFrame(tryPrint);
        return;
      }
      autoPrintDone.current = true;
      runPrint(receipt);
    };
    const frame = requestAnimationFrame(tryPrint);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
    // Print once when a new sale opens on a connected printer, or when autoPrint is set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, isReprintMode, autoPrint]);

  const handlePrint = () => runPrint(receipt);

  const handleReprint = async () => {
    if (!receipt.order_id || reprinting) return;
    setReprinting(true);
    setReprintError('');
    setPrintNotice('');
    setPrintWarning('');
    try {
      const data = await getPosSale(receipt.order_id, token);
      const nextReceipt = normalizePosSalePayload(data, {
        ...receipt,
        cashier_name: receipt.cashier_name || user?.name,
      });
      setReceipt(nextReceipt);
      await runPrint(nextReceipt);
    } catch (err) {
      setReprintError(err.message || 'Could not reprint this receipt.');
    } finally {
      setReprinting(false);
    }
  };

  return createPortal(
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <div className={styles.confirmHead}>
          <span className={styles.checkIcon}><CheckCircle2 size={22} /></span>
          <div>
            <p className={styles.confirmTitleRow}>
              <span className={styles.confirmTitle}>
                {isProvisional
                  ? 'Provisional slip'
                  : isReprintMode
                  ? (isVoided ? 'Reprint voided receipt' : isRevised ? 'Reprint revised receipt' : 'Reprint receipt')
                  : (isVoided ? 'Bill voided' : isRevised ? 'Bill revised' : 'Sale complete')}
              </span>
              {isVoided && <span className={styles.voidedBadge}>Voided</span>}
              {!isVoided && isRevised && (
                <span className={styles.revisedBadge}>
                  Revised{revisionLabel ? ` · r${revisionLabel}` : ''}
                </span>
              )}
            </p>
            <p className={styles.confirmSub}>
              {receipt.display_id}
              {isRevised && revisionLabel ? ` · rev ${revisionLabel}` : ''}
              {' · '}
              {formatCurrency(receipt.total)}
            </p>
          </div>
        </div>

        <div className={styles.receipt} id="pos-receipt">
          <div className={styles.receiptHead}>
            <img src={bhimaMartLogo} alt="Bhima Mart" className={styles.storeLogo} />
            <p className={styles.storeMeta}>Praneeth Pranav Jaitra, ClubHouse Ground Floor</p>
            <p className={styles.storeMeta}>GSTIN: {gstin}</p>
            {isVoided && <p className={styles.voidedBanner}>VOID</p>}
            {!isVoided && isRevised && (
              <>
                <p className={styles.revisedBanner}>REVISED</p>
                {revisionLabel && (
                  <p className={styles.revisedRevision}>Revision {revisionLabel}</p>
                )}
              </>
            )}
            <p className={styles.billTitle}>Bill</p>
          </div>

          <div className={styles.divider} />

          <div className={styles.billMeta}>
            <span className={styles.billMetaItem}>Counter: {counter}</span>
            <span className={styles.billMetaItem}>Bill.no: {receipt.display_id}</span>
            {isRevised && revisionLabel && (
              <span className={styles.billMetaItem}>Rev: {revisionLabel}</span>
            )}
            {isVoided && receipt.void_reason && (
              <span className={styles.billMetaWide}>Void reason: {receipt.void_reason}</span>
            )}
            <span className={styles.billMetaItem}>Date: {dateShort} {timeShort}</span>
            <span className={styles.billMetaItem}>User: {cashierName}</span>
          </div>

          <div className={styles.divider} />

          <div className={styles.billMeta}>
            <span className={styles.billMetaWide}>Customer No: {customerNumber}</span>
            {customerName && (
              <span className={styles.billMetaWide}>Name: {customerName}</span>
            )}
            {customerPhone && (
              <span className={styles.billMetaWide}>Phone: {customerPhone}</span>
            )}
            {isPayLater && addressLine1 && (
              <span className={styles.billMetaWide}>Address: {addressLine1}</span>
            )}
            {isPayLater && addressLine2 && (
              <span className={styles.billMetaWide}>{addressLine2}</span>
            )}
            {isPayLater && (block || flat) && (
              <span className={styles.billMetaWide}>
                {[block && `Blk ${block}`, flat && `Flat ${flat}`].filter(Boolean).join(', ')}
              </span>
            )}
          </div>

          <div className={styles.divider} />

          <div className={styles.itemsHead}>
            <span>Item</span>
            <span className={styles.center}>Qty</span>
            <span className={styles.right}>Amount</span>
          </div>
          {lineItems.map((it) => {
            const mrp = getItemMrp(it, catalogProducts);
            const savings = getItemSavings(it, mrp);
            const savingsText = formatItemSavingsText(savings, it, formatCurrency);

            return (
              <div key={`${it.item_id}-${it.sku}-${it.qty}`} className={`${styles.itemRow} ${styles.lineItemRow}`}>
                <span className={styles.itemName}>
                  {it.name}
                  <span className={styles.itemUnit}>
                    {unitPriceLabel(it, formatCurrency)}
                    {savingsText && (
                      <span className={styles.itemSavingsText}>
                        {' · '}{savingsText}
                      </span>
                    )}
                  </span>
                </span>
                <span className={styles.center}>{formatLineQty(it)}</span>
                <span className={styles.right}>{formatCurrency(it.line_total)}</span>
              </div>
            );
          })}

          <div className={styles.divider} />

          <div className={styles.sumRow}><span>Total Items/Qty</span><span>{itemsQtySummary}</span></div>
          <div className={styles.sumRow}><span>Subtotal</span><span>{formatCurrency(receipt.subtotal)}</span></div>
          {(receipt.discount_amount ?? 0) > 0 && (
            <div className={styles.sumRow}>
              <span>{formatDiscountLabel(receipt)}</span>
              <span>−{formatCurrency(receipt.discount_amount)}</span>
            </div>
          )}
          {roundOff !== 0 && (
            <div className={styles.sumRow}>
              <span>Round off</span>
              <span>{formatRoundOff(roundOff)}</span>
            </div>
          )}
          <div className={`${styles.sumRow} ${styles.sumTotal}`}>
            <span>To pay</span>
            <span>{formatCurrency(receipt.total)}</span>
          </div>
          {billSavings.totalSavings > 0 && (
            <div className={`${styles.sumRow} ${styles.sumSavings}`}>
              <span>Total Savings</span>
              <span>
                {formatCurrency(billSavings.totalSavings)}
                {billSavings.totalSavingsPct > 0 ? ` (${formatDiscountPercent(billSavings.totalSavingsPct)})` : ''}
              </span>
            </div>
          )}
          <p className={styles.gstInclusive}>
            {!receipt.note || isProvisional || /^PAY LATER\b/i.test(receipt.note)
              ? '(Inclusive of GST)'
              : receipt.note}
          </p>

          {!isProvisional && taxedSlabs.length > 0 && (
            <>
              <div className={styles.divider} />
              <div className={styles.itemsHead}>
                <span>GST</span>
                <span className={styles.center}>CGST</span>
                <span className={styles.right}>SGST</span>
              </div>
              {taxedSlabs.map((g) => (
                <div key={g.rate} className={styles.itemRow}>
                  <span className={styles.itemName}>
                    {g.rate}%
                    <span className={styles.itemUnit}>on {formatCurrency(g.taxable)}</span>
                  </span>
                  <span className={styles.center}>{formatCurrency(g.cgst)}</span>
                  <span className={styles.right}>{formatCurrency(g.sgst)}</span>
                </div>
              ))}
              <div className={styles.sumRow}><span>Total GST</span><span>{formatCurrency(totalGst)}</span></div>
            </>
          )}

          <div className={styles.divider} />

          {isPayLater ? (
            <>
              <p className={styles.payLaterNote}>{payLaterNote}</p>
              {payLater.amountPaid > 0 && methodBreakdown.length < 2 && (
                <div className={styles.sumRow}>
                  <span>
                    {payLaterCollected ? 'Paid' : 'Paid now'}
                    {payLater.lastMethod ? ` via ${PAYMENT_LABEL[payLater.lastMethod]}` : ''}
                  </span>
                  <span>
                    {formatCurrency(payLaterCollected ? payLater.amountPaid : (payLater.lastAmount || payLater.amountPaid))}
                  </span>
                </div>
              )}
              {!payLaterCollected && (
                <div className={styles.sumRow}>
                  <span>Amount due</span>
                  <span>{formatCurrency(payLater.outstanding)}</span>
                </div>
              )}
              {methodBreakdown.length > 1 && methodBreakdown.map((line) => (
                <div key={line.method} className={styles.sumRow}>
                  <span>{line.label}</span>
                  <span>{formatCurrency(line.amount)}</span>
                </div>
              ))}
              {payLater.lastRoundOff !== 0 && (
                <div className={styles.sumRow}>
                  <span>Cash round off</span>
                  <span>{formatRoundOff(payLater.lastRoundOff)}</span>
                </div>
              )}
              {cashierNotes.map((text) => (
                <p key={text} className={styles.cashierNote}>Note: {text}</p>
              ))}
            </>
          ) : (
            tenderLines.length > 1 ? (
              tenderLines.map((line) => (
                <div key={line.method} className={styles.sumRow}>
                  <span>Paid via {PAYMENT_LABEL[line.method] || line.method}</span>
                  <span>{formatCurrency(line.amount)}</span>
                </div>
              ))
            ) : (
              <div className={styles.sumRow}>
                <span>Paid via {PAYMENT_LABEL[receipt.payment_method] || receipt.payment_method}</span>
                <span>{formatCurrency(receipt.total)}</span>
              </div>
            )
          )}
          {changeDue > 0 && (
            <div className={styles.sumRow}><span>Change</span><span>{formatCurrency(changeDue)}</span></div>
          )}
          {amountReturned > 0 && (
            <div className={styles.sumRow}><span>Amount returned</span><span>{formatCurrency(amountReturned)}</span></div>
          )}

          {billSavings.totalSavings > 0 && (
            <p className={styles.savingsCallout}>
              You saved {formatCurrency(billSavings.totalSavings)} on this order!
            </p>
          )}

          <p className={styles.feedbackNote}>Give Us feedback on bhimasupermarkets@svyminds.com</p>
          <p className={styles.thankYou}>Thank you for shopping with us!</p>
        </div>

        {printNotice && <p className={styles.printNotice}>{printNotice}</p>}
        {printWarning && <p className={styles.printWarning}>{printWarning}</p>}
        {reprintError && <p className={styles.reprintError}>{reprintError}</p>}

        <div className={styles.actionsFooter}>
          {isReprintMode ? (
            <>
              <Button
                variant="outline"
                className={styles.cancelBtn}
                icon={Printer}
                onClick={printViaBrowser}
                disabled={reprinting || printing}
              >
                {printing ? 'Printing…' : 'Print'}
              </Button>
              <div className={styles.actions}>
                <Button variant="outline" onClick={onClose}>
                  {closeLabel}
                </Button>
              </div>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                className={styles.cancelBtn}
                icon={Printer}
                onClick={handlePrint}
                disabled={printing}
              >
                {printing ? 'Printing…' : connected ? 'Print again' : 'Print receipt'}
              </Button>
              <div className={styles.actions}>
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
                <Button icon={Plus} onClick={onNewSale || onClose}>
                  Start new sale
                </Button>
              </div>
            </>
          )}
        </div>

        {!isReprintMode && isConfirmedSale && printAttempted && !connected && (
          <p className={styles.reprintHint}>
            Print didn&apos;t work?{' '}
            <button type="button" className={styles.reprintLink} onClick={handleReprint} disabled={reprinting}>
              {reprinting ? 'Fetching…' : 'Reprint from server'}
            </button>
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
}
